import AWS, { S3 } from "aws-sdk";
import API, { GraphQLResult, graphqlOperation } from "@aws-amplify/api";
import { listProjects, getProject } from "../api/graphql/queries";
import {
  ListProjectsQueryVariables,
  GetProjectQuery,
  ListProjectsQuery,
} from "./API";
import {
  Billing,
  Dataset,
  KeyValue,
  Log,
  Process,
  Project,
} from "../api/models";
import Amplify, { Auth } from "aws-amplify";
import awsconfig from "../api/aws-exports";
import { DataStore } from "aws-amplify";
import { xor } from "lodash";
import { updateProject } from "./graphql/mutations";

export enum Role {
  ADMIN = "Admin",
  COLLABORATOR = "Collaborator",
  CONTRIBUTOR = "Contributor",
  NONE = "None",
}
export class DataPortalService {
  public static async getJWT() {
    const response = await Auth.currentSession();
    return JSON.parse(
      atob(response.getAccessToken().getJwtToken().split(".")[1])
    );
  }

  public static formatFileSize(bytes, si = false, dp = 1) {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
      return bytes + " B";
    }
    const units = si
      ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
      : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
    let u = -1;
    const r = 10 ** dp;
    do {
      bytes /= thresh;
      ++u;
    } while (
      Math.round(Math.abs(bytes) * r) / r >= thresh &&
      u < units.length - 1
    );
    return bytes.toFixed(dp) + " " + units[u];
  }

  public static async getDatasetFiles(projectId, datasetId) {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${(await Auth.currentSession())
        .getAccessToken()
        .getJwtToken()}`,
    };
    const objects = await API.get("AdminQueries", "/listObjects", {
      queryStringParameters: {
        project: `z-${projectId}`,
        dataset: datasetId,
      },
      headers,
    });

    const prefix = `datasets/${datasetId}/`;
    const prefixLength = prefix.length;

    const files = objects.map((v) => {
      const fileUri = `root/${v.Key?.substr(prefixLength) || ""}`;
      const parts = fileUri.split("/");
      const name = parts.pop();
      const parent = parts[parts.length - 1];
      const path = parts.join("/");
      const obj = v.Key;
      return {
        type: "file",
        parent,
        name,
        path,
        obj,
        date: v.LastModified,
        size: DataPortalService.formatFileSize(v.Size),
      };
    });
    const folders = Array.from(
      files.reduce((p, c) => {
        const folders = c.path.split("/");
        while (folders.length) {
          const folder = folders.pop();
          if (folders.length) {
            p.add(`${folder}:${folders[folders.length - 1]}`);
          }
        }
        return p;
      }, new Set())
    )
      .map((folder) => {
        const parts = String(folder).split(":");
        return {
          type: "folder",
          parent: parts[1],
          name: parts[0],
          path: "",
          size: "",
        };
      })
      .filter((v) => v.name !== "");
    const data = [...files, ...folders];
    return data;
  }

  public static async permissionsRevoke(
    username: String,
    projectId: String,
    role: Role
  ) {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${(await Auth.currentSession())
        .getAccessToken()
        .getJwtToken()}`,
    };
    const admin = await API.post("AdminQueries", "/removeUserFromGroup", {
      body: {
        username,
        groupname: `z-${projectId}-${role}`,
      },
      headers,
    });
  }
  public static async permissionsGrant(
    username: String,
    projectId: String,
    role: Role
  ) {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${(await Auth.currentSession())
        .getAccessToken()
        .getJwtToken()}`,
    };
    const admin = await API.post("AdminQueries", "/addUserToGroup", {
      body: {
        username,
        groupname: `z-${projectId}-${role}`,
      },
      headers,
    });
  }

  // Return The Users Roll - By Querying JWT Token and extracting / marshalling the cognito group
  public static async getRole(projectId: string): Promise<Role> {
    const jwt = await DataPortalService.getJWT();
    const group = jwt["cognito:groups"].filter((v) =>
      v.startsWith(`z-${projectId}`)
    );
    const roll = group[0].replace(`z-${projectId}-`, "");
    return roll === "admin"
      ? Role.ADMIN
      : roll === "collaborator"
      ? Role.COLLABORATOR
      : roll === "contributor"
      ? Role.CONTRIBUTOR
      : Role.NONE;
  }

  public static subscribe(type: Project, callback: any) {
    DataStore.observe(type).subscribe(callback);
  }
  public static async setDatasetTags(dataset: Dataset, tags: Array<KeyValue>) {
    const rv = await DataStore.save(
      Dataset.copyOf(dataset, (update) => {
        update.tags = tags;
      })
    );
    return rv;
  }
  public static async setProjectTags(project: Project, tags: Array<KeyValue>) {
    const rv = await DataStore.save(
      Project.copyOf(project, (update) => {
        update.tags = tags;
      })
    );
    return rv;
  }
  public static async getBilling(
    projectId: string,
    year?: number,
    month?: number,
    day?: number
  ) {
    year = year || new Date().getUTCFullYear();
    month = month || new Date().getUTCMonth() + 1;
    day = day || 1;
    const billingId = `${projectId}--${year.toString()}-${month
      .toString()
      .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
    const billings = (await DataStore.query<Billing>(Billing, billingId)) || {
      infoJson: "{}",
    };
    const billingInfo = JSON.parse(billings.infoJson || "{}");
    const rv = Object.keys(billingInfo).map((v) => {
      return {
        key: v.replace("Amazon", ""),
        value: billingInfo[v].UnblendedCost.Amount,
      };
    });
    return rv;
  }

  private static cognito: AWS.CognitoIdentityServiceProvider | null = null;

  public static async getCognito() {
    if (!this.cognito) {
      const credentials = await Auth.currentCredentials();
      this.cognito = new AWS.CognitoIdentityServiceProvider({
        credentials,
        region: awsconfig.aws_project_region,
      });
    }
    return this.cognito;
  }

  public static async getObjectUrl(projectId: string, objPath: string) {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${(await Auth.currentSession())
        .getAccessToken()
        .getJwtToken()}`,
    };
    const url = await API.get("AdminQueries", "/downloadUrl", {
      queryStringParameters: {
        groupname: `z-${projectId}`,
        objectname: objPath,
      },
      headers,
    });
    return url;
  }

  public static async getUsersByName(prefix?: string) {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${(await Auth.currentSession())
        .getAccessToken()
        .getJwtToken()}`,
    };
    const users = await API.get("AdminQueries", "/listUsers", {
      queryStringParameters: {
        prefix: prefix,
        limit: 10,
      },
      headers,
    });
    return users.Users.map((v) => v.Username);
  }
  public static async getUsersByProject(projectID) {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${(await Auth.currentSession())
        .getAccessToken()
        .getJwtToken()}`,
    };
    const admin = await API.get("AdminQueries", "/listUsersInGroup", {
      queryStringParameters: {
        groupname: `z-${projectID}-admin`,
      },
      headers,
    });
    const collab = await API.get("AdminQueries", "/listUsersInGroup", {
      queryStringParameters: {
        groupname: `z-${projectID}-collaborator`,
      },
      headers,
    });

    const contrib = await API.get("AdminQueries", "/listUsersInGroup", {
      queryStringParameters: {
        groupname: `z-${projectID}-contributor`,
      },
      headers,
    });

    const users = [
      ...admin.Users.map((v) => ({ username: v.Username, role: Role.ADMIN })),
      ...collab.Users.map((v) => ({
        username: v.Username,
        role: Role.COLLABORATOR,
      })),
      ...contrib.Users.map((v) => ({
        username: v.Username,
        role: Role.CONTRIBUTOR,
      })),
    ];
    return users;
  }

  public static async getDatasets(project?: Project) {
    return project
      ? (await DataStore.query<Dataset>(Dataset)).filter(
          (c) => c.projectRef?.id === project.id
        )
      : await DataStore.query<Dataset>(Dataset);
  }

  public static async getDataset(datasetId: string) {
    return await DataStore.query<Dataset>(Dataset, datasetId);
  }

  public static async getProjects() {
    return await DataStore.query<Project>(Project);
  }

  public static async getProject(projectId: string) {
    return await DataStore.query<Project>(Project, projectId);
  }

  public static async getLogs(dataset?: Dataset) {
    return dataset
      ? (await DataStore.query<Log>(Log)).filter((c) => {
          c.dataset === dataset.id;
        })
      : await DataStore.query<Log>(Log);
  }

  public static async getProcesses(process: Process): Promise<Array<Process>> {
    const children = process.childProcessIds || [];
    const processes = await DataStore.query<Process>(Process);
    const output = processes.filter((v) => children.includes(v.id));
    console.dir(output);
    return output;
  }
  public static async getProcess(project): Promise<Process> {
    const processes = await DataStore.query<Process>(Process);
    return processes.find((v) => v.id === project.process) as Process;
  }

  private static async isAuthenitcated(): Promise<boolean> {
    let status: boolean = false;

    await Auth.currentSession()
      .then((data) => (status = true))
      .catch((err) => console.log("ERROR: ", err.message));

    return status;
  }

  // public static async listFiles(s3Path: string): Promise<any> {
  //   const humanFileSize = (bytes, si = false, dp = 1) => {
  //     const thresh = si ? 1000 : 1024;

  //     if (Math.abs(bytes) < thresh) {
  //       return bytes + " B";
  //     }
  //     const units = si
  //       ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
  //       : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  //     let u = -1;
  //     const r = 10 ** dp;
  //     do {
  //       bytes /= thresh;
  //       ++u;
  //     } while (
  //       Math.round(Math.abs(bytes) * r) / r >= thresh &&
  //       u < units.length - 1
  //     );
  //     return bytes.toFixed(dp) + " " + units[u];
  //   };
  //   return new Promise(async (resolve, reject) => {
  //     const s3 = await this.getStorageClient();
  //     s3.listObjectsV2(this.parseS3Path(s3Path), (error, data) => {
  //       if (error) {
  //         reject(error);
  //       } else {
  //         const prefixLength = data.Prefix.length;
  //         const files = (
  //           data.Contents?.map((v) => {
  //             const fileUri = `root/${v.Key?.substr(prefixLength) || ""}`;
  //             const parts = fileUri.split("/");
  //             const name = parts.pop();
  //             const parent = parts[parts.length - 1];
  //             const path = parts.join("/");
  //             return {
  //               type: "file",
  //               parent,
  //               name,
  //               path,
  //               date: v.LastModified,
  //               size: humanFileSize(v.Size),
  //             };
  //           }) || []
  //         ).filter((v) => v.name !== "");

  //         const folders = Array.from(
  //           files.reduce((p, c) => {
  //             const folders = c.path.split("/");
  //             while (folders.length) {
  //               const folder = folders.pop();
  //               if (folders.length) {
  //                 p.add(`${folder}:${folders[folders.length - 1]}`);
  //               }
  //             }
  //             return p;
  //           }, new Set())
  //         )
  //           .map((folder) => {
  //             const parts = String(folder).split(":");
  //             return {
  //               type: "folder",
  //               parent: parts[1],
  //               name: parts[0],
  //               path: "",
  //               size: "",
  //             };
  //           })
  //           .filter((v) => v.name !== "");
  //         resolve([...files, ...folders]);
  //       }
  //     });
  //   });
  // }

  // // get the bucket and prefix from the s3 path
  // private static parseS3Key(s3Path: string): { Bucket: string; Key: string } {
  //   const _s3Path: string = s3Path.replace(/^s3:\/\//, "");
  //   const firstSlashPos: number = _s3Path.indexOf("/");
  //   let bucket: string;
  //   let key: string;
  //   if (firstSlashPos > 0) {
  //     bucket = _s3Path.substring(0, firstSlashPos);
  //     key = _s3Path.substring(firstSlashPos + 1, _s3Path.length);
  //   } else {
  //     bucket = _s3Path;
  //     key = "";
  //   }
  //   return {
  //     Bucket: bucket,
  //     Key: key,
  //   };
  // }
  // private static parseS3Path(
  //   s3Path: string
  // ): { Bucket: string; Prefix: string } {
  //   const _s3Path: string = s3Path.replace(/^s3:\/\//, "");
  //   const firstSlashPos: number = _s3Path.indexOf("/");
  //   let bucket: string;
  //   let prefix: string;
  //   if (firstSlashPos > 0) {
  //     bucket = _s3Path.substring(0, firstSlashPos);
  //     prefix = _s3Path.substring(firstSlashPos + 1, _s3Path.length);
  //   } else {
  //     bucket = _s3Path;
  //     prefix = "";
  //   }
  //   return {
  //     Bucket: bucket,
  //     Prefix: prefix,
  //   };
  // }

  public static async redirectHostedUI(): Promise<void> {
    const authenticated = await this.isAuthenitcated();
    const cognitoDomain = awsconfig["oauth"]["domain"];
    const redirectURI = awsconfig["oauth"]["redirectSignIn"];
    const responseType = awsconfig["oauth"]["responseType"];
    const webClientId = awsconfig["aws_user_pools_web_client_id"];

    //redirect to cognito hosted ui if code parameter is not available
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    if (authenticated === false && code === null) {
      // Redirtect user-agent to /authorize endpoint
      window.location.href =
        "https://" +
        cognitoDomain +
        "/login?response_type=" +
        responseType +
        "&client_id=" +
        webClientId +
        "&redirect_uri=" +
        redirectURI;
    }
  }
}

// import AWS from "aws-sdk";
// import Amplify, { API, Auth, graphqlOperation } from "aws-amplify";
// import { CognitoUser } from "@aws-amplify/auth";
// import { GraphQLResult } from "@aws-amplify/api";
// import * as APITS from "./API";
// import { ISignUpResult } from "amazon-cognito-identity-js";
// import * as queries from './graphql/queries';

// // fileDownload module
// import fileDownload from "js-file-download";

// // read amplify config
// import awsconfig from "../templates/aws-exports";
// Amplify.configure(awsconfig);

// export type User = { name: string };
// export type Project = { id: string; name: string; cognitoGroup: string };
// export type Analysis = { id: string; name: string };
// export type Config = { id: string; name: string };

// // export type Permission = {}
// export type Dataset = {
//   id: string;
//   name: string;
//   collaboratorsView: string[];
//   s3path: string;
//   project: {
//     id: string;
//     name: string;
//     adminUsers: string[];
//     users: string[];
//     collaboratorsView: string[];
//   };
// };

// // this is for cognito sigin
// export enum domainType {
//   azureAd = "FHCRC",
//   aws = "COGNITO",
// }

// // SAML Id provider
// export enum samlProvider {
//   prod = "fh-azure-ad",
//   dev = "fh-dvc-sso",
// }

// // this is for executing Graphql queries
// export interface GraphQLOptions {
//   input?: object;
// }

// export class DataPortalService {

//   public static storageClient: AWS.S3;
//   public static cognitoISP: AWS.CognitoIdentityServiceProvider;
//   public static cognitoSAML: string = samlProvider.prod;

//   /* **************************************************
//    * Cognito
//    * **************************************************/

//   // signup for a cognito account
//   public static async signUp(
//     username: string,
//     password: string
//   ): Promise<ISignUpResult> {
//     return await Auth.signUp(username, password);
//   }

//   // confirm sign up code
//   public static async confirmSignUp(
//     username: string,
//     code: string
//   ): Promise<void> {
//     return await Auth.confirmSignUp(username, code);
//   }

//   // resend signup confirmation code
//   public static async resendConfirmationCode(
//     username: string
//   ): Promise<string> {
//     return await Auth.resendSignUp(username);
//   }

//   // check if user is authenticated to cognito
//   private static async isAuthenitcated(): Promise<boolean> {
//     let status: boolean = false;

//     await Auth.currentSession()
//       .then((data) => (status = true))
//       .catch((err) => console.log("ERROR: ", err.message));

//     return status;
//   }

//   // use AWS Cognito Hosted UI for federated and/or cognito signin

//   // cognito login
//   public static async login(
//     username: string,
//     password: string,
//     domain: domainType
//   ): Promise<CognitoUser> {
//     return await Auth.signIn(username, password);
//   }

//   // cognito sign out, default to sign out to all
//   public static async logout(): Promise<void> {
//     return await Auth.signOut({ global: true });
//   }

//   // get the authenticated user's username
//   public static async getCurrentUser(): Promise<string> {
//     let user = "";

//     await Auth.currentAuthenticatedUser().then((data) => {
//       user = data.username;
//     });

//     return user;
//   }

//   // get user credential then use it to instantiate a cognito identity provoider
//   public static async setCognitoISP(): Promise<void> {
//     let cognitoidentityserviceprovider;

//     AWS.config.apiVersions = {
//       cognitoidentityserviceprovider: "2016-04-18",
//     };

//     //const resp: AWS.Credentials = (await Auth.currentCredentials()) as AWS.Credentials;
//     await Auth.currentCredentials().then((data) => {
//       cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider({
//         credentials: data,
//         region: awsconfig.aws_project_region,
//       });
//     });

//     this.cognitoISP = cognitoidentityserviceprovider;
//   }

//   // public static grantUserPermission(
//   //   user: User,
//   //   project: Dataset | Project,
//   //   permission: Permission
//   // ): boolean {
//   //   return false
//   // }

//   // add a cognito user to a cognito group
//   //
//   // note: a cognito group is created for each project
//   public static grantUserPermission(
//     user: string,
//     project: Project,
//     callback: (status: boolean) => void
//   ): void {
//     DataPortalService.isCognitoUser(user, (icuStatus) => {
//       // user not found in user pool, add it
//       if (icuStatus === false) {
//         this.addCognitoUser(user, (acuStatus) => {
//           // user successfully added, now add him/her to the group
//           if (acuStatus === true) {
//             this.addUserToGroup(user, project?.cognitoGroup, (autgStatus) => {
//               callback(autgStatus);
//               // return status of adding user to group
//             });
//           } else {
//             callback(acuStatus);
//             // failed to add user
//           }
//         });
//         // cognito user found, add it to the project
//       } else {
//         this.addUserToGroup(user, project?.cognitoGroup, (autgStatus) => {
//           callback(autgStatus);
//           // return status of adding user to group
//         });
//       }
//     });
//   }

//   // add user to a cognito group
//   private static addUserToGroup(
//     user: string,
//     group: string,
//     callback: (status: boolean) => void
//   ): void {
//     // add user to the cognito group
//     const params = {
//       GroupName: group /* required */,
//       UserPoolId: awsconfig.aws_user_pools_id /* required */,
//       Username: user,
//     };

//     this.cognitoISP.adminAddUserToGroup(params, function (err, data) {
//       if (err) {
//         console.log(`ERROR: ${err.message}`);
//         callback(false);
//         // an error occurred
//       } else {
//         callback(true);
//       } // successful response
//     });
//   }

//   // check if user is added to the cognito pool
//   private static isCognitoUser(
//     user: string,
//     callback: (status: boolean) => void
//   ): void {
//     const params = {
//       UserPoolId: awsconfig.aws_user_pools_id /* required */,
//       Username: user,
//     };

//     this.cognitoISP.adminGetUser(params, (err, data) => {
//       if (err) callback(false);
//       else callback(true);
//     });
//   }

//   // check if the user is in a group
//   private static isUserinGroup(
//     user: string,
//     group: string,
//     callback: (status: boolean) => void
//   ): void {
//     const params = {
//       UserPoolId: awsconfig.aws_user_pools_id /* required */,
//       Username: user,
//     };

//     this.cognitoISP.adminListGroupsForUser(params, (err, data) => {
//       if (err) callback(false);
//       else callback(true);
//     });
//   }

//   // add user to cognito pool
//   public static addCognitoUser(
//     user: string,
//     callback: (status: boolean) => void
//   ): void {
//     // get a random password with 20 characters
//     const tempPW = this.getPassword(20);

//     const params = {
//       UserPoolId: awsconfig.aws_user_pools_id /* required */,
//       Username: user /* required */,
//       DesiredDeliveryMediums: ["EMAIL"],
//       ForceAliasCreation: false,
//       TemporaryPassword: tempPW,
//       UserAttributes: [
//         {
//           Name: "email" /* required */,
//           Value: user /* required */,
//         },
//         {
//           Name: "name",
//           Value: user,
//         },
//         {
//           Name: "email_verified",
//           Value: "True",
//         },
//         {
//           Name: "phone_number",
//           Value: "+15555555555",
//         },
//         {
//           Name: "phone_number_verified",
//           Value: "True",
//         },
//       ],
//     };

//     this.cognitoISP.adminCreateUser(params, (err, data) => {
//       if (err) {
//         console.log("ERROR: [create cognito user] ", err.message);
//         callback(false);
//       } else {
//         this.linkFedAccountForUser(user, (status) => {
//           callback(status);
//         });
//       }
//     });
//   }

//   // merge federated user with existing cognito user
//   public static linkFedAccountForUser(user: string, callback): void {
//     const params = {
//       UserPoolId: awsconfig.aws_user_pools_id /* required */,

//       /* required */
//       DestinationUser: {
//         ProviderAttributeName: "UserName",
//         ProviderAttributeValue: user,
//         ProviderName: "Cognito",
//       },
//       /* required */
//       SourceUser: {
//         ProviderAttributeName: "Cognito_Subject",
//         ProviderAttributeValue: user,
//         ProviderName: this.cognitoSAML,
//       },
//     };

//     this.cognitoISP.adminLinkProviderForUser(params, (err, data) => {
//       if (err) {
//         console.log("ERROR: [link user account]", err.message);
//         callback(false);
//       } else {
//         callback(true);
//       }
//     });
//   }

//   // launch client email client for a signup email
//   private static mailNewUser(user: string): void {
//     const subject = "Data portal signup";
//     const body = "Please go to 127.0.0.1 to signup for data portal";
//     window.open(`mailto:  ${user}?subject=${subject}&body=${body}`);
//   }

//   // generate a random password for new user
//   private static getPassword(passwordLength: number): string {
//     let pw = "";
//     const pwChars =
//       "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";

//     for (let i: number = 0; i < passwordLength; i++) {
//       let c = pwChars.charAt(Math.floor(Math.random() * pwChars.length));
//       pw += c;
//     }

//     return pw;
//   }

//   // public static updateUserPermission(
//   //   user: User,
//   //   project: Dataset | Project,
//   //   permission: Permission
//   // ): boolean {
//   //   return false
//   // }

//   // public static revokeUserPermission(
//   //   user: User,
//   //   project: Dataset | Project
//   // ): boolean {
//   //   return false
//   // }

//   // remove a cognito user from a cognito group
//   //
//   // note: a cognito group is created for each project
//   public static revokeUserPermission(
//     user: string,
//     project: Project,
//     callback: (status: boolean) => void
//   ): void {
//     const params = {
//       GroupName: project?.cognitoGroup /* required */,
//       UserPoolId: awsconfig.aws_user_pools_id /* required */,
//       Username: user,
//     };

//     this.cognitoISP.adminRemoveUserFromGroup(params, function (err, data) {
//       if (err) {
//         console.log(`ERROR: ${err.message}`);
//         callback(false);
//         // an error occurred
//       } else {
//         callback(true);
//       } // successful response
//     });
//   }

//   // check if user in the Admin cognito group
//   public static async isFHRCRAdminUser(): Promise<boolean> {
//     let userGroups: string[] = [];

//     await Auth.currentUserPoolUser().then(async (resp) => {
//       userGroups = resp.signInUserSession.accessToken.payload["cognito:groups"];
//     });

//     if (userGroups.includes("Admin")) {
//       return true;
//     } else {
//       return false;
//     }
//   }

//   /* **************************************************
//    * GraphQL
//    * **************************************************/

//   // helper method to make graphql call
//   private static async callGraphql<T>(
//     query: any,
//     options?: GraphQLOptions
//   ): Promise<GraphQLResult<T>> {
//     return (await API.graphql(
//       graphqlOperation(query, options)
//     )) as GraphQLResult<T>;
//   }

//   // retrieve all projects or a project by the project id
//   public static async getProjects(projectID?: string): Promise<Project[]> {
//     queries.listProjects
//     const resp =
//     // let filter = "";

//     // // pull only a project?
//     // if (projectID !== undefined) {
//     //   filter = `
//     //     ( filter: { id: { eq: "${projectID}" } } )
//     //   `;
//     // }

//     // // select the fields to match the Project type
//     // const ListProjectsQuery = `query ListProjects {
//     //       listProjects ${filter} {
//     //         items {
//     //           id
//     //           name
//     //           createdAt
//     //           description
//     //           users
//     //           collaboratorsView
//     //           adminUsers
//     //           cognitoGroup
//     //         }
//     //       }
//     //     }`;

//     // const resp = await this.callGraphql<APITS.ListProjectsQuery>(
//     //   ListProjectsQuery
//     // );
//     // return resp.data.listProjects.items || [];
//   }

//   // admin user of a project who can add/remove users from the project cognito group
//   public static async getProjectAdminUsers(project: Project): Promise<[]> {
//     const GetProjectQuery = `query GetProject {
//       getProject(id: "${project.id}") {
//         adminUsers
//       }
//     }
//     `;

//     // only user in the Admin group or the project group can retrieve that data
//     // of that group.  Hence the catch block return a empty list, since the user querying
//     // the record does not belong to the group and certainly not an admin user
//     const resp = await this.callGraphql<APITS.GetProjectQuery>(GetProjectQuery);
//     return resp.data.getProject.adminUsers || [];
//   }

//   // check if user in the Admin cognito group or listed in the AdminUser field
//   // if either is true, this user is an admin user who can manipulate the data
//   public static async isProjectAdminUser(project: Project): Promise<boolean> {
//     let status = false;
//     let currentUser = "";

//     await this.isFHRCRAdminUser().then(async (resp: boolean) => {
//       if (resp === true) {
//         status = true;
//         // stop here, he/she is an admin user, don't need to check further
//       } else {
//         // okay, he/she is not an admin user, check if he/she is in the adminUsers field
//         await this.getCurrentUser().then(async (user) => {
//           currentUser = user;
//           await this.getProjectAdminUsers(project).then(
//             (adminUsers: string[]) => {
//               if (adminUsers.includes(currentUser)) {
//                 status = true;
//               }
//             }
//           );
//         });
//       }
//     });

//     return status;
//   }

//   // need to review this method, get the dataset by project id or dataset id
//   public static async getDatasets(
//     project?: Project | String
//   ): Promise<Dataset[]> {
//     let filter = "";

//     // check if we want to pull datasets of a project
//     if (project !== undefined) {
//       const id =
//         typeof project === "string" || project instanceof String
//           ? project
//           : project.id;
//       filter = `
//       (
//         filter:  {projectID: {eq: "${id}"}}
//       )
//       `;
//     }

//     // select datasets the user is allowed to view
//     const ListDatasetsQuery = `query ListDatasets {
//           listDatasets ${filter} {
//             items {
//                  id
//                  name
//                  collaboratorsView
//                  s3path
//                  datasets
//                  desc
//                  date
//                  status
//             }
//           }
//         }`;

//     const resp = await this.callGraphql<APITS.ListDatasetsQuery>(
//       ListDatasetsQuery
//     );

//     return resp.data.listDatasets.items || [];
//   }

//   // get a dataset by dataset ID
//   public static async getSingleDataset(datasetID: string): Promise<Dataset> {
//     // get dataset the user is allowed to view by dataset ID
//     const GetDatasetQuery = `query GetDataset {
//           getDataset(id: "${datasetID}") {
//                  id
//                  name
//                  collaboratorsView
//                  s3path
//                  project {
//                      id
//                      name
//                      adminUsers
//                      users
//                      collaboratorsView
//                  }
//           }
//         }`;

//     const resp = await this.callGraphql<APITS.GetDatasetQuery>(GetDatasetQuery);

//     return resp.data.getDataset || {};
//   }

//   // get the children datasets of a specific dataset
//   public static async getChildDatasets(datasetID: string): Promise<Dataset[]> {
//     // get dataset the user is allowed to view by dataset ID
//     const ListDatasetsQuery = `query ListDatasets {
//           listDatasets(filter: {
//             datasets: {
//               contains: "${datasetID}"
//             }
//           }) {
//             items {
//               id
//               name
//             }
//           }
//         }`;

//     const resp = await this.callGraphql<APITS.ListDatasetsQuery>(
//       ListDatasetsQuery
//     );

//     return resp.data.listDatasets.items || [];
//   }

//   // get an analysis of a dataset
//   public static async getAnalyses(dataset: Dataset): Promise<Analysis> {
//     // get analysis of dataset
//     const GetDatasetQuery = `query GetDataset {
//           getDataset (id: "${dataset.id}") {
//             configuration {
//               analysis {
//                 id
//                 name
//               }
//             }
//           }
//         }`;

//     const resp = this.callGraphql<APITS.GetDatasetQuery>(GetDatasetQuery);

//     return resp.data.getDataset.configuration.analysis | {};
//   }

//   // update users of a project
//   public static async updateProjectUsers(
//     projectID: string,
//     users: string[]
//   ): Promise<string[]> {
//     const resp = await this.callGraphql<APITS.UpdateProjectMutation>(
//       updateProject,
//       {
//         input: { id: projectID, users },
//       }
//     );

//     return resp.data.updateProject.users || [];
//   }

//   // public static getUsers(project: Project | Dataset): User[] {
//   //     return [];
//   // }

//   // get users of a project

//   public static async findUsers(project: Project | String): Promise<User[]> {
//     const id =
//       typeof project === "string" || project instanceof String
//         ? project
//         : project.id;
//     const GetProjectUsersQuery = `query GetProject {
//       getProject (id: "${id}") {
//         users
//       }
//     }`;
//     const resp = await this.callGraphql<APITS.GetProjectQuery>(
//       GetProjectUsersQuery
//     );

//     return resp.data.getProject.users || [];
//   }

//   // Update Project Users + Collaborators
//   public static async updateProjectUsers(
//     dataset: Dataset,
//     users: string[],
//     collaborators: string[]
//   ): Promise<any> {
//     await this.callGraphql<APITS.UpdateDatasetMutation>(updateProject, {
//       input: { id: dataset.id, collaboratorsView: collaborators, users: users },
//     });
//   }

//   // update the collaborators of dataset
//   public static async updateDatasetCollaboratorView(
//     dataset: Dataset,
//     collabUsers: string[]
//   ): Promise<string[]> {
//     const resp = await this.callGraphql<APITS.UpdateDatasetMutation>(
//       updateDataset,
//       {
//         input: { id: dataset.id, collaboratorsView: collabUsers },
//       }
//     );

//     return resp.data.updateDataset.collaboratorsView || [];
//   }

//   // update the collaborators of project
//   public static async updateProjectCollaboratorView(
//     project: Project,
//     collabUsers: string[]
//   ): Promise<string[]> {
//     const resp = await this.callGraphql<APITS.UpdateProjectMutation>(
//       updateProject,
//       {
//         input: { id: project.id, collaboratorsView: collabUsers },
//       }
//     );

//     return resp.data?.updateProject?.collaboratorsView || [];
//   }

//   /* **************************************************
//    * S3
//    * **************************************************/
//   // set the s3 client using user's credentials
//   public static async setStorageClient(): Promise<void> {
//     const myConf = new AWS.Config({
//       credentials: null,
//       region: awsconfig.aws_project_region,
//       apiVersion: "2006-03-01",
//     });

//     const s3 = new AWS.S3();

//     await Auth.currentCredentials()
//       .then((cred) => {
//         myConf.credentials = cred;
//       })
//       .then((data) => {
//         s3.config = myConf;
//       });

//     this.storageClient = s3;
//   }

//   // get a list of files of the give s3 path
//   //
//   // note: a callback is used to retrieve the output
//   public static async listFiles(
//     s3Path: string,
//     callback: (data: string[] | void) => void
//   ): Promise<void> {
//     const [bucket, prefix] = this.parseS3Path(s3Path);

//     const params = {
//       Bucket: bucket,
//       Prefix: prefix,
//     };

//     await this.storageClient.listObjectsV2(params, (err, data) => {
//       if (err) {
//         console.log(`ERROR: ${err.message}`);
//       } else {
//         try {
//           callback(data.Contents?.map((item) => bucket + "/" + item.Key));
//         } catch (e) {
//           console.log(`ERROR: ${e.message}`);
//           callback([]);
//         }
//       }
//     });
//   }

//   // public static zipAndDownloadFiles(project: Project): boolean {
//   //   return false
//   // }

//   // public static downloadFile(): boolean {
//   //   return false
//   // }

//   // download file when user click on the file
//   public static downloadFile(s3Path: string): void {
//     const [bucket, prefix] = this.parseS3Path(s3Path);
//     let file = "";

//     if (prefix.includes("/")) {
//       const fileKeyRX = prefix.match(/(.*)\/(.*)/);
//       file = fileKeyRX[2];
//     } else {
//       file = prefix;
//     }

//     const params = {
//       Bucket: bucket,
//       Key: prefix,
//     };

//     this.storageClient.getObject(params, function (err, data) {
//       if (err) console.log(`ERROR: ${err.message}`);
//       else {
//         fileDownload(data.Body, file);
//       }
//     });
//   }

//   // public static runAnalysis(dataset: Dataset, config: Config): boolean {
//   //   return false
//   // }
// }
